"use client";
import React, { useEffect, useState } from "react";
import dynamic from "next/dynamic";
import Link from "next/link";

import "../../marken/marken.css";

import { TopSectionMarken } from "../../marken/sections/TopSection";
import { AutoVerkaufenGrid } from "@/common/AutoVerkaufenGrid";
import { Banner } from "@/common/Banner";
import sellCarCash from "@/assets/auto-verkaufen/Rückbank-autoverkauf.avif";

import höchstenPreis from "@/assets/auto-verkaufen/Frau-verkauft-ihr-auto.webp";
import BannerImage1 from "@/assets/auto-verkaufen/AutoeinfachlosBannerBig1.avif";
import BannerImage2 from "@/assets/auto-verkaufen/AutoeinfachlosBannerBig2.avif";
import BildTextInfo from "@/components/BildTextInfo/BildTextInfo";

import { HäufigeFragen } from "@/common/HäufigeFragen";
import FAQImage from "@/assets/faq-autoverkauf-autoeinfachlos.webp";
import { fragenGewaehrleistungPrivatverkaufAuto } from "./data";

// Ratgeber SERVERSEITIG rendern (dynamic OHNE die SSR-Abschaltung, Playbook 2.6):
// die sechs "Mehr zum Thema"-Kacheln verlinken u. a. /auto-verkaufen.
const Ratgeber = dynamic(() =>
  import("@/components/Ratgeber/Ratgeber").then((m) => m.default)
);

// Only needed when the user reaches step 12; avoid loading auth-related JS/CSS on initial paint.
const AnmeldenSection = dynamic(() => import("@/app/anmelden/AnmeldenStepRedirect"), {
  ssr: false,
});

function GewaehrleistungPrivatverkaufAuto() {
  const [step, setStep] = useState(1);
  // selected values
  const [selectedBrand, setSelectedBrand] = useState({ id: "", name: "" });
  const [selectedModel, setSelectedModel] = useState({ id: "", name: "" });
  const [selectedYear, setSelectedYear] = useState({ id: "", name: "" });
  const [selectedBauform, setSelectedBauform] = useState({ id: "", name: "" });
  const [selectedFuel, setSelectedFuel] = useState({ id: "", name: "" });
  const [selectedVariant, setSelectedVariant] = useState({ id: "", name: "" });
  const [selectedPs, setSelectedPs] = useState({ id: "", name: "" });
  const [selectedTransmission, setSelectedTransmission] = useState({
    id: "",
    name: "",
  });
  const [selectedDoors, setSelectedDoors] = useState({ id: "", name: "" });
  const [selectedModelType, setSelectedModelType] = useState({
    id: "",
    name: "",
  });
  const [selectedKmStand, setSelectedKmStand] = useState("");
  const isSecondaryMethod = true;

  useEffect(() => {
    if (step >= 10) {
      if (typeof AnmeldenSection?.preload === "function") {
        AnmeldenSection.preload();
      } else {
        import("@/app/anmelden/AnmeldenStepRedirect");
      }
    }
  }, [step]);


  return (
    <div className="home-container">
      <TopSectionMarken
        title="Gewährleistung beim Privatverkauf Auto – das musst Du wissen"
        link="/guides/gewaehrleistung-privatverkauf-auto"
        linkname="Gewährleistung Privatverkauf Auto"
        text={
          <>
            84% der Autoeinfachlos-Kunden bekommen mehr Geld.*{" "}
            <br className="desktop" />
            Hol dir deine kostenlose und sofortige Bewertung.
          </>
        }
        removePadding={true}
        step={step}
        setStep={setStep}
        selectedBrand={selectedBrand}
        setSelectedBrand={setSelectedBrand}
        selectedModel={selectedModel}
        setSelectedModel={setSelectedModel}
        selectedYear={selectedYear}
        setSelectedYear={setSelectedYear}
        selectedBauform={selectedBauform}
        setSelectedBauform={setSelectedBauform}
        selectedFuel={selectedFuel}
        setSelectedFuel={setSelectedFuel}
        selectedVariant={selectedVariant}
        setSelectedVariant={setSelectedVariant}
        selectedPs={selectedPs}
        setSelectedPs={setSelectedPs}
        selectedTransmission={selectedTransmission}
        setSelectedTransmission={setSelectedTransmission}
        selectedDoors={selectedDoors}
        setSelectedDoors={setSelectedDoors}
        setSelectedModelType={setSelectedModelType}
        selectedModelType={selectedModelType}
        selectedKmStand={selectedKmStand}
        setSelectedKmStand={setSelectedKmStand}
      />

      {step === 12 && (
        <>
          <AnmeldenSection
            title="Bitte gib deine E-Mail-Adresse ein, um fortzufahren"
            fin={null}
            isManual={isSecondaryMethod}
            marke={selectedBrand}
            model={selectedModel}
            baujahr={selectedYear}
            bodytype={selectedBauform}
            fueltype={selectedFuel}
            variant={selectedVariant}
            ps={selectedPs}
            gearbox={selectedTransmission}
            doors={selectedDoors}
            modelType={selectedModelType}
            kmStand={selectedKmStand}
            page="/auto-verkaufen"
          />
          <Banner removeContent className="Banner-anmelden" />
        </>
      )}

      {step !== 12 && (
        <div className="Page background-color">
          <div className="Page-wrapper">
            <AutoVerkaufenGrid
              image={sellCarCash}
              leftTitle="Was fällt unter die Gewährleistung beim Auto?"
              leftText={
                <>
                  <p>
                    Beim Privatverkauf eines Autos denken viele: „Verkauft ist
                    verkauft.“ Doch ganz so einfach ist es nicht. Auch private
                    Verkäufer tragen eine gesetzliche Gewährleistungspflicht, wenn
                    im Kaufvertrag nichts anderes vereinbart wurde.
                  </p>
                  <p>
                    Dieser Artikel erklärt dir, was das genau bedeutet, wann du
                    haftest und wie du dich vor bösen Überraschungen schützt.
                    Außerdem erfährst du, warum der Verkauf über Autoeinfachlos
                    die sicherere Alternative sein kann.
                  </p>
                </>
              }
              rightFirstTitle="Was bedeutet Gewährleistung beim Privatverkauf eines Autos?"
              rightFirstText={
                <>
                  <p>
                    Die Gewährleistung ist die gesetzliche Pflicht, für Mängel am
                    Fahrzeug zu haften, die bereits beim Verkauf bestanden, auch
                    wenn sie erst später auffallen.
                  </p>
                  <p>
                    Das gilt grundsätzlich für alle Verkäufe, also auch zwischen
                    Privatpersonen.
                  </p>
                  <p>
                    Im Unterschied zur Garantie (freiwillige Zusage des
                    Herstellers oder Händlers) ist die Gewährleistung gesetzlich
                    geregelt (§ 922 ABGB).
                  </p>
                  <p>
                    Der Käufer kann bei Mängeln Reparatur, Preisminderung oder
                    Rücktritt vom Vertrag verlangen – sofern keine wirksame
                    Vereinbarung etwas anderes vorsieht.
                  </p>

                  <h3>Kann man die Gewährleistung beim Privatverkauf ausschließen?</h3>
                  <p>
                    Ja und das ist beim Privatverkauf üblich und rechtlich
                    zulässig, solange keine arglistige Täuschung vorliegt.
                  </p>
                  <p>
                    Das bedeutet: Du darfst keine bekannten Mängel verschweigen
                    oder falsche Angaben machen.
                  </p>
                  <p>
                    Eine wirksame Formulierung im Kaufvertrag lautet zum Beispiel:
                  </p>
                  <p>
                    „Das Fahrzeug wird unter Ausschluss jeglicher Gewährleistung
                    verkauft. Der Verkäufer haftet nicht für offene oder verdeckte
                    Mängel.“
                  </p>
                  <p>
                    Wichtig ist, dass diese Formulierung schriftlich im
                    Kaufvertrag steht. Nur dann bist du rechtlich auf der sicheren
                    Seite.
                  </p>
                </>
              }
              rightSecondTitle="Diese Risiken bestehen beim privaten Autoverkauf"
              rightSecondText={
                <>
                  <p>
                    Viele unterschätzen, dass ein Privatverkauf auch rechtliche
                    Risiken birgt.
                  </p>
                  <p>
                    Selbst mit Gewährleistungsausschluss kann es zu Problemen
                    kommen, wenn der Käufer behauptet, du hättest Mängel
                    verschwiegen. Typische Beispiele:
                  </p>
                  <ul className="custom-unordered-list secondary">
                    <li>Motorschaden kurz nach dem Verkauf</li>
                    <li>Unfallvorschäden nicht angegeben</li>
                    <li>Tachomanipulation vermutet</li>
                    <li>Fahrzeug entspricht nicht der Beschreibung</li>
                  </ul>
                  <p>
                    In solchen Fällen drohen Rückabwicklung, Klagen oder Kosten
                    für Nachbesserungen.
                  </p>
                  <p>
                    Gerade bei älteren oder reparaturanfälligen Fahrzeugen kann
                    das teuer werden.
                  </p>
                </>
              }
            />

            <div className="Banner-sections">
              <BildTextInfo
                image={BannerImage1}
                revert
                h2
                isBig
                fontSize="36px"
                title="So schützt Du Dich beim Privatverkauf"
                alt="So schützt Du Dich beim Privatverkauf"
                text={
                  <>
                    <p>
                      Wenn du dein Auto selbst verkaufen möchtest, solltest du
                      unbedingt folgende Punkte beachten:
                    </p>
                    <ul className="custom-unordered-list">
                      <li>
                        Verwende einen schriftlichen Kaufvertrag (am besten mit
                        Gewährleistungsausschluss).
                      </li>
                      <li>
                        Beschreibe den Fahrzeugzustand ehrlich und vollständig.
                      </li>
                      <li>
                        Mache Fotos von Karosserie, Kilometerstand und
                        Ausstattung.
                      </li>
                      <li>
                        Lass dir die Zahlung nachvollziehbar (z. B. per
                        Überweisung) geben.
                      </li>
                      <li>
                        Dokumentiere die Fahrzeugübergabe (Datum, Uhrzeit,
                        Zeugen).
                      </li>
                    </ul>
                    <p>
                      So reduzierst du das Risiko, nachträglich haftbar gemacht zu
                      werden.
                    </p>
                  </>
                }
              />

              <BildTextInfo
                image={BannerImage2}
                h2
                isBig
                fontSize="36px"
                title="Alternative zum Privatverkauf – sicher über Händlernetzwerk verkaufen"
                alt="Alternative zum Privatverkauf"
                text={
                  <>
                    <p>
                      Wenn du auf Nummer sicher gehen willst, ist der Verkauf über
                      ein professionelles Händlernetzwerk wie Autoeinfachlos die
                      beste Lösung.
                    </p>
                    <p>
                      Hier bist du komplett von der Gewährleistungspflicht
                      befreit, denn der Verkauf erfolgt rechtssicher über geprüfte
                      Händler.
                    </p>
                    <p>
                      <strong>Deine Vorteile:</strong>
                    </p>
                    <ul className="custom-unordered-list">
                      <li>Kein rechtliches Risiko oder Streit mit Käufern</li>
                      <li>
                        Faire Preisangebote durch über 5.000 Händler in ganz
                        Österreich
                      </li>
                      <li>Kein Aufwand mit Inseraten oder Probefahrten</li>
                      <li>Schnelle Auszahlung nach Verkaufsabschluss</li>
                      <li>Auf Wunsch inklusive Abmeldung und Abholung</li>
                    </ul>
                    <p>
                      Gerade bei älteren Fahrzeugen, Motorschäden oder Zeitdruck
                      sparst du dir so jede Menge Stress und bist rechtlich auf
                      der sicheren Seite.
                    </p>
                  </>
                }
              />
            </div>

            <HäufigeFragen
              title="Häufige Fragen zur Gewährleistung beim Privatverkauf Auto"
              fragen={fragenGewaehrleistungPrivatverkaufAuto}
              image={FAQImage}
            />

            <div style={{ paddingTop: "120px" }}>
              <Ratgeber
                title="Mehr zum Thema Gewährleistung beim privaten Autoverkauf"
                kaputtesAuto
                unfallAuto
                autoExport
                autoBewerten
                autoVerkaufen
                listenpreisBerechnen
              />
            </div>

            <div className="Banner-sections-marken">
              <BildTextInfo
                image={höchstenPreis}
                h2
                fontSize="36px"
                title="Sicher verkaufen ohne Risiko"
                alt="Sicher verkaufen ohne Risiko mit Autoeinfachlos"
                text={
                  <>
                    <p>
                      Beim Privatverkauf eines Autos gilt: Gewährleistung ist kein
                      optionales Thema, sondern eine rechtliche Pflicht, die du
                      nur mit klarer Formulierung ausschließen kannst.
                    </p>
                    <p>
                      Wer keine Lust auf Unsicherheit, Streit oder juristische
                      Fallstricke hat, sollte sein Fahrzeug lieber über ein
                      geprüftes Händlernetzwerk verkaufen.
                    </p>
                    <p>
                      <Link className="link" href="/auto-bewerten">
                        Jetzt kostenlos Fahrzeugwert ermitteln und sicher über
                        Autoeinfachlos verkaufen – ganz ohne Gewährleistungsrisiko.
                      </Link>
                    </p>
                  </>
                }
              />
            </div>
          </div>
          <Banner
            link="/auto-verkaufen"
            title="Jetzt Auto kostenlos bewerten & sicher verkaufen"
          />
        </div>
      )}
    </div>
  );
}

export default GewaehrleistungPrivatverkaufAuto;
